const initFestivalHeaderSearch = () => {
    document.querySelectorAll('[data-festival-header-search]').forEach((form) => {
        const input = form.querySelector('[data-festival-header-search-input]');
        const results = form.querySelector('[data-festival-header-search-results]');
        const endpoint = form.dataset.suggestionsUrl;
        if (!input || !results || !endpoint) return;

        if (!results.id) {
            results.id = `ff-member-festival-suggestions-${crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`;
        }
        input.setAttribute('aria-controls', results.id);

        const existingBinding = form._festivalHeaderSearchBinding;
        if (existingBinding?.input === input && existingBinding?.results === results && existingBinding?.endpoint === endpoint) return;
        existingBinding?.abort();

        const bindingController = new AbortController();
        const { signal } = bindingController;
        form._festivalHeaderSearchBinding = {
            input,
            results,
            endpoint,
            abort: () => bindingController.abort(),
        };

        const ghost = document.createElement('span');
        ghost.className = 'ff-member-festival-search-ghost';
        ghost.setAttribute('aria-hidden', 'true');
        input.insertAdjacentElement('afterend', ghost);
        let completion = '';
        const clearCompletion = () => {
            completion = '';
            ghost.textContent = '';
            form.classList.remove('has-inline-completion');
        };
        const updateCompletion = (items, query) => {
            const typed = input.value;
            const first = items.find((item) => typeof item.name === 'string' && item.name.toLocaleLowerCase().startsWith(query.toLocaleLowerCase()));
            if (!first || !typed || input.selectionStart !== typed.length || input.selectionEnd !== typed.length) return clearCompletion();
            completion = first.name;
            ghost.replaceChildren();
            const prefix = document.createElement('span');
            prefix.className = 'ff-member-festival-search-ghost-prefix';
            prefix.textContent = typed;
            const suffix = document.createElement('span');
            suffix.textContent = first.name.slice(typed.length);
            ghost.append(prefix, suffix);
            form.classList.add('has-inline-completion');
        };
        const acceptCompletion = () => {
            if (!completion) return false;
            input.value = completion;
            input.setSelectionRange(completion.length, completion.length);
            clearCompletion();
            input.dispatchEvent(new Event('input', { bubbles: true }));
            return true;
        };
        let timer;
        let controller;
        const close = () => {
            results.hidden = true;
            results.replaceChildren();
            input.setAttribute('aria-expanded', 'false');
            input.removeAttribute('aria-activedescendant');
            clearCompletion();
        };
        const dismissSuggestions = () => {
            clearTimeout(timer);
            controller?.abort();
            close();
        };
        input.addEventListener('input', () => {
            clearTimeout(timer);
            if (controller) controller.abort();
            const query = input.value.trim();
            clearCompletion();
            if (query.length < 2) return close();
            timer = window.setTimeout(async () => {
                controller = new AbortController();
                const requestController = controller;
                try {
                    const response = await fetch(endpoint + '?search=' + encodeURIComponent(query), {
                        headers: { Accept: 'application/json' },
                        signal: requestController.signal,
                    });
                    if (requestController.signal.aborted || input.value.trim() !== query) return;
                    if (!response.ok) return close();
                    const payload = await response.json();
                    if (requestController.signal.aborted || input.value.trim() !== query) return;
                    const items = Array.isArray(payload.data) ? payload.data : [];
                    if (!items.length) return close();
                    updateCompletion(items, query);
                    results.replaceChildren(...items.map((item, index) => {
                        const link = document.createElement('a');
                        link.className = 'ff-member-festival-suggestion';
                        link.href = item.url;
                        link.setAttribute('role', 'option');
                        link.setAttribute('aria-selected', 'false');
                        link.id = `${results.id}-option-${index}`;
                        if (item.thumbnail) {
                            const image = document.createElement('img');
                            image.className = 'ff-member-festival-suggestion-thumbnail';
                            image.src = item.thumbnail;
                            image.alt = '';
                            image.loading = 'lazy';
                            link.append(image);
                        } else {
                            const fallback = document.createElement('span');
                            fallback.className = 'ff-member-festival-suggestion-thumbnail ff-member-festival-suggestion-thumbnail--fallback';
                            fallback.setAttribute('aria-hidden', 'true');
                            fallback.textContent = '♫';
                            link.append(fallback);
                        }
                        const copy = document.createElement('span');
                        copy.className = 'ff-member-festival-suggestion-copy';
                        const name = document.createElement('span');
                        name.className = 'ff-member-festival-suggestion-name';
                        name.textContent = item.name || '';
                        const meta = document.createElement('span');
                        meta.className = 'ff-member-festival-suggestion-meta';
                        meta.textContent = [item.location, item.date].filter(Boolean).join(' · ');
                        copy.append(name, meta);
                        link.append(copy);
                        return link;
                    }));
                    results.hidden = false;
                    input.setAttribute('aria-expanded', 'true');
                } catch (error) {
                    if (error.name !== 'AbortError') close();
                }
            }, 180);
        }, { signal });
        input.addEventListener('keydown', (event) => {
            if ((event.key === 'Tab' || event.key === 'ArrowRight') && completion && input.selectionStart === input.value.length) {
                event.preventDefault();
                acceptCompletion();
                return;
            }
            if (event.key === 'Escape') dismissSuggestions();
            if (event.key === 'ArrowDown' && results.hidden && input.value.trim().length >= 2) {
                event.preventDefault();
                input.dispatchEvent(new Event('input', { bubbles: true }));
                return;
            }
            if (event.key === 'ArrowDown' && !results.hidden) {
                event.preventDefault();
                const firstOption = results.querySelector('a');
                firstOption?.focus();
                if (firstOption) input.setAttribute('aria-activedescendant', firstOption.id);
            }
        }, { signal });
        const syncActiveOption = () => {
            const activeOption = document.activeElement?.closest?.('.ff-member-festival-suggestion');
            results.querySelectorAll('[role="option"]').forEach((option) => {
                option.setAttribute('aria-selected', option === activeOption ? 'true' : 'false');
            });
            if (activeOption && results.contains(activeOption)) input.setAttribute('aria-activedescendant', activeOption.id);
            else input.removeAttribute('aria-activedescendant');
        };
        results.addEventListener('focusin', syncActiveOption, { signal });
        form.addEventListener('keydown', (event) => {
            const options = [...results.querySelectorAll('a')];
            const index = options.indexOf(document.activeElement);
            if (event.key === 'ArrowDown' && index >= 0) {
                event.preventDefault();
                options[Math.min(index + 1, options.length - 1)]?.focus();
                syncActiveOption();
            } else if (event.key === 'ArrowUp' && index >= 0) {
                event.preventDefault();
                if (index === 0) input.focus(); else options[index - 1]?.focus();
                syncActiveOption();
            } else if (event.key === 'Home' && index >= 0) {
                event.preventDefault();
                options[0]?.focus();
                syncActiveOption();
            } else if (event.key === 'End' && index >= 0) {
                event.preventDefault();
                options[options.length - 1]?.focus();
                syncActiveOption();
            } else if (event.key === 'Escape') {
                dismissSuggestions();
                input.focus();
            }
        }, { signal });
        form.addEventListener('submit', dismissSuggestions, { signal });
        form.addEventListener('focusout', (event) => {
            if (!event.relatedTarget || !form.contains(event.relatedTarget)) dismissSuggestions();
        }, { signal });
        document.addEventListener('pointerdown', (event) => {
            if (!form.contains(event.target)) dismissSuggestions();
        }, { signal });
        signal.addEventListener('abort', () => {
            clearTimeout(timer);
            controller?.abort();
            ghost.remove();
            form.classList.remove('has-inline-completion');
        }, { once: true });
    });
};
document.addEventListener('DOMContentLoaded', initFestivalHeaderSearch);
document.addEventListener('livewire:navigated', initFestivalHeaderSearch);
